import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import {
  ArrowLeft,
  Tv,
  FolderOpen,
  ListVideo,
  CalendarClock,
  BarChart3,
  Activity,
  Wifi,
  Film,
  PlayCircle,
} from "lucide-react";

const SAMPLE_SCREENS = [
  { id: "s1", name: "Lobby TV", location: "Bengaluru · Indiranagar", status: "online", orientation: "landscape", last_seen: "2 min ago" },
  { id: "s2", name: "Front Window", location: "Mumbai · Bandra", status: "online", orientation: "portrait", last_seen: "just now" },
  { id: "s3", name: "Counter Display", location: "Delhi · Saket", status: "online", orientation: "landscape", last_seen: "5 min ago" },
  { id: "s4", name: "Drive-Thru", location: "Hyderabad · Gachibowli", status: "offline", orientation: "landscape", last_seen: "2h ago" },
];

const SAMPLE_MEDIA = [
  { id: "m1", name: "Times Square", type: "image", url: "https://images.unsplash.com/photo-1543853016-2ce0a3a55e7c?auto=format&fit=crop&w=600&q=70" },
  { id: "m2", name: "Tokyo Neon", type: "image", url: "https://images.unsplash.com/photo-1542051841857-5f90071e7989?auto=format&fit=crop&w=600&q=70" },
  { id: "m3", name: "London Skyline", type: "image", url: "https://images.unsplash.com/photo-1505761671935-60b3a7427bad?auto=format&fit=crop&w=600&q=70" },
  { id: "m4", name: "Holiday Promo", type: "image", url: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?auto=format&fit=crop&w=600&q=70" },
  { id: "m5", name: "Brand Anthem", type: "image", url: "https://images.unsplash.com/photo-1558655146-d09347e92766?auto=format&fit=crop&w=600&q=70" },
  { id: "m6", name: "Marina Bay", type: "image", url: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?auto=format&fit=crop&w=600&q=70" },
];

const SAMPLE_PLAYLISTS = [
  { id: "p1", name: "Morning Loop", items: 4, screens: 3 },
  { id: "p2", name: "Lunch Promos", items: 3, screens: 2 },
  { id: "p3", name: "Evening Brand", items: 5, screens: 4 },
];

const SAMPLE_SCHEDULES = [
  { id: "sc1", playlist: "Morning Loop", screens: ["Lobby TV", "Front Window"], days: "Mon–Fri", hours: "07:00 – 11:00", active: true },
  { id: "sc2", playlist: "Lunch Promos", screens: ["Counter Display"], days: "Daily", hours: "11:00 – 15:00", active: true },
  { id: "sc3", playlist: "Evening Brand", screens: ["Lobby TV", "Front Window", "Counter Display", "Drive-Thru"], days: "Daily", hours: "17:00 – 23:00", active: true },
];

const SAMPLE_PER_DAY = [40, 65, 80, 55, 110, 130, 95];
const SAMPLE_TOP_MEDIA = [
  { name: "Holiday Promo", plays: 218 },
  { name: "Brand Anthem", plays: 184 },
  { name: "Times Square", plays: 152 },
  { name: "Tokyo Neon", plays: 121 },
];

const TABS = [
  { key: "screens", label: "Screens", icon: Tv },
  { key: "media", label: "Media", icon: FolderOpen },
  { key: "playlists", label: "Playlists", icon: ListVideo },
  { key: "schedules", label: "Schedules", icon: CalendarClock },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
];

export default function DemoSignage() {
  const [tab, setTab] = useState("screens");

  return (
    <div className="bg-[#0A0908] text-white min-h-screen font-sans">
      <header className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <BrandLogo size={30} />
            <span className="font-display font-extrabold tracking-tight text-[16px]">Mirrorloops</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/demo" className="text-sm text-white/60 hover:text-white flex items-center gap-2">
              <ArrowLeft size={14} /> All demos
            </Link>
            <Link to="/register" className="text-xs px-3 py-1.5 bg-white text-black font-semibold">
              Get started
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#E8D6A3] mb-3 flex items-center gap-2">
          <Tv size={12} /> Demo · Signage management
        </div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display font-black tracking-tighter leading-[0.92] text-4xl md:text-6xl">
              Your screens.
              <br />
              <span className="text-white/30">A guided demo.</span>
            </h1>
            <p className="mt-4 text-white/60 max-w-xl">
              Pre-loaded with 4 screens, 6 media, 3 playlists, 3 schedules
              and a week of analytics. Click any tab — exactly what your
              operators will use.
            </p>
          </div>
          <a
            href="/play/demo"
            target="_blank"
            rel="noreferrer"
            data-testid="demo-sig-open-player"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#D4B878]/40 text-[#E8D6A3] hover:bg-[#D4B878] hover:text-black transition font-semibold"
          >
            <PlayCircle size={14} /> Open the demo player
          </a>
        </div>

        {/* Tabs */}
        <div className="mt-10 flex flex-wrap gap-1 bg-white/[0.02] border border-white/10 p-1" data-testid="demo-sig-tabs">
          {TABS.map((t) => {
            const I = t.icon;
            const on = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`demo-sig-tab-${t.key}`}
                className={`flex items-center gap-2 px-4 py-2 text-sm transition ${
                  on ? "bg-[#D4B878] text-black font-semibold" : "text-white/60 hover:text-white"
                }`}
              >
                <I size={14} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8 min-h-[400px]">
          {tab === "screens" && <ScreensTab />}
          {tab === "media" && <MediaTab />}
          {tab === "playlists" && <PlaylistsTab />}
          {tab === "schedules" && <SchedulesTab />}
          {tab === "analytics" && <AnalyticsTab />}
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-[11px] text-white/30">
        © Mirrorloops Opc Pvt. Ltd.  ·  Sample data — sign up to manage real screens
      </footer>
    </div>
  );
}

function ScreensTab() {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="demo-sig-screens">
      {SAMPLE_SCREENS.map((s) => (
        <div key={s.id} className="border border-white/10 bg-white/[0.02] p-5 hover:border-white/30 transition">
          <div className="flex items-center justify-between mb-3">
            <Tv size={18} className="text-[#E8D6A3]" />
            <span
              className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 border ${
                s.status === "online"
                  ? "border-emerald-500/40 text-emerald-400"
                  : "border-zinc-600 text-zinc-500"
              }`}
            >
              ● {s.status}
            </span>
          </div>
          <div className="font-display font-extrabold text-xl">{s.name}</div>
          <div className="text-xs text-white/40 mt-1">{s.location}</div>
          <div className="mt-4 flex items-center justify-between text-[11px] font-mono text-white/40">
            <span>{s.orientation}</span>
            <span>{s.last_seen}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function MediaTab() {
  return (
    <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="demo-sig-media">
      {SAMPLE_MEDIA.map((m) => (
        <div key={m.id} className="overflow-hidden border border-white/10 group">
          <div className="aspect-square bg-black overflow-hidden">
            <img src={m.url} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition" />
          </div>
          <div className="p-2">
            <div className="text-xs font-semibold truncate">{m.name}</div>
            <div className="text-[10px] text-white/40 uppercase tracking-[0.18em] mt-0.5">{m.type}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PlaylistsTab() {
  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="demo-sig-playlists">
      {SAMPLE_PLAYLISTS.map((p) => (
        <div key={p.id} className="border border-white/10 bg-white/[0.02] p-5 hover:border-white/30 transition">
          <div className="flex items-center justify-between mb-3">
            <ListVideo size={18} className="text-[#E8D6A3]" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-white/40">
              Playlist
            </span>
          </div>
          <div className="font-display font-extrabold text-2xl">{p.name}</div>
          <div className="mt-4 grid grid-cols-2 gap-3 font-mono text-xs">
            <div>
              <div className="text-white/40 uppercase tracking-[0.18em] text-[10px]">Items</div>
              <div className="text-lg">{p.items}</div>
            </div>
            <div>
              <div className="text-white/40 uppercase tracking-[0.18em] text-[10px]">Screens</div>
              <div className="text-lg">{p.screens}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SchedulesTab() {
  return (
    <div className="border border-white/10 bg-white/[0.02]" data-testid="demo-sig-schedules">
      {SAMPLE_SCHEDULES.map((sc, i) => (
        <div
          key={sc.id}
          className={`p-5 grid md:grid-cols-[1.2fr_1fr_1fr_auto] gap-4 items-center ${
            i < SAMPLE_SCHEDULES.length - 1 ? "border-b border-white/5" : ""
          }`}
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1">Playlist</div>
            <div className="font-bold">{sc.playlist}</div>
          </div>
          <div className="text-xs text-white/60">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1">Screens</div>
            {sc.screens.join(" · ")}
          </div>
          <div className="text-xs text-white/60">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1">When</div>
            {sc.days} · {sc.hours}
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 border border-emerald-500/40 text-emerald-400">
              Active
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function AnalyticsTab() {
  const max = Math.max(...SAMPLE_PER_DAY);
  const totalPlays = SAMPLE_PER_DAY.reduce((a, b) => a + b, 0);
  const maxTop = SAMPLE_TOP_MEDIA[0].plays;
  return (
    <div className="space-y-6" data-testid="demo-sig-analytics">
      <div className="grid md:grid-cols-4 gap-4">
        <KPI icon={<Film size={16} />} label="Total plays" value={totalPlays.toString()} accent />
        <KPI icon={<Wifi size={16} />} label="Online" value={`${SAMPLE_SCREENS.filter(s=>s.status==='online').length} / ${SAMPLE_SCREENS.length}`} />
        <KPI icon={<Tv size={16} />} label="Active screens" value={SAMPLE_SCREENS.length.toString()} />
        <KPI icon={<FolderOpen size={16} />} label="Media playing" value={SAMPLE_TOP_MEDIA.length.toString()} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-white/10 bg-white/[0.02] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-4 flex items-center gap-1">
            <Activity size={12} /> Top media
          </div>
          {SAMPLE_TOP_MEDIA.map((m, i) => {
            const pct = (m.plays / maxTop) * 100;
            return (
              <div key={m.name} className="flex items-center gap-3 mb-2">
                <span className="text-[10px] text-white/40 w-4">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{m.name}</div>
                  <div className="h-1.5 bg-white/5 mt-1 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#D4B878] to-[#E8D6A3]"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <div className="text-sm font-bold text-[#E8D6A3]">{m.plays}</div>
              </div>
            );
          })}
        </div>
        <div className="border border-white/10 bg-white/[0.02] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-4">Last 7 days</div>
          <div className="flex items-end gap-2 h-40">
            {SAMPLE_PER_DAY.map((v, i) => {
              const h = Math.max(8, (v / max) * 100);
              const days = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className="w-full bg-gradient-to-t from-[#D4B878] to-[#E8D6A3]"
                    style={{ height: `${h}%` }}
                    title={`${days[i]}: ${v}`}
                  />
                  <div className="text-[10px] text-white/40">{days[i]}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ icon, label, value, accent }) {
  return (
    <div className={`p-5 border ${accent ? "border-[#D4B878]/30 bg-gradient-to-br from-[#1a1508] to-[#0c0c0c]" : "border-white/10 bg-white/[0.02]"}`}>
      <div className="text-[#E8D6A3] mb-2">{icon}</div>
      <div className="text-2xl font-black">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-1">{label}</div>
    </div>
  );
}
